if (!customElements.get('tabbed-content')) {
  class Tabs extends HTMLElement {
    constructor() {
      super();
      this.tabList = this.querySelector('[role="tablist"]');
      this.activeTab = this.tabList.querySelector('[aria-selected="true"]');
      this.isVerticalTablist = this.tabList.getAttribute('aria-orientation') === 'vertical';
      this.tabs = this.querySelectorAll('[role="tab"]');
      this.panels = this.querySelectorAll('[role="tabpanel"]');
      this.isProductDetails = this.hasAttribute('data-preserve-scroll');

      if (!this.activeTab) {
        this.activeTab = this.tabs[0];
      }

      this.tabs.forEach((t) => {
        if (t === this.activeTab) {
          t.removeAttribute('tabindex');
          Tabs.setTabState(t, true, this);
        } else {
          t.setAttribute('tabindex', '-1');
          Tabs.setTabState(t, false, this);
        }
      });

      this.addListeners();
    }

    addListeners() {
      if (this.isProductDetails) {
        this.handlePointerDownBound = this.handlePointerDown.bind(this);
        this.tabList.addEventListener('pointerdown', this.handlePointerDownBound);
        this.tabList.addEventListener('mousedown', this.handlePointerDownBound);
      }
      this.tabList.addEventListener('click', this.handleClick.bind(this));
      this.tabList.addEventListener('keydown', this.handleKeydown.bind(this));
    }

    /**
     * Prevents tab buttons from receiving focus on click/tap.
     * @param {PointerEvent} evt - Event object.
     */
    handlePointerDown(evt) {
      if (!evt.target.closest('[role="tab"]')) return;
      evt.preventDefault();
    }

    /**
     * Handles 'click' events on the tablist.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      const tab = evt.target.closest('[role="tab"]');
      if (!tab || tab === this.activeTab) return;
      evt.preventDefault();
      this.activateTab(tab, false);
    }

    /**
     * Handles 'keydown' events on the tablist.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      switch (evt.key) {
        case 'ArrowLeft':
        case 'ArrowRight':
          evt.preventDefault();
          if (!this.isVerticalTablist) {
            this.switchTabOnKeyPress(evt.key);
          }
          break;

        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();
          if (this.isVerticalTablist) {
            this.switchTabOnKeyPress(evt.key);
          }
          break;

        case 'Home':
          evt.preventDefault();
          this.activateTab(this.tabs[0], true);
          break;

        case 'End':
          evt.preventDefault();
          this.activateTab(this.tabs[this.tabs.length - 1], true);
          break;

        default:
          break;
      }
    }

    /**
     * Activates the next or previous tab according to the key pressed.
     * @param {string} key - Key pressed.
     */
    switchTabOnKeyPress(key) {
      if (key === 'ArrowRight' || key === 'ArrowDown') {
        if (this.activeTab === this.tabs[this.tabs.length - 1]) {
          this.activateTab(this.tabs[0], true);
        } else {
          this.activateTab(this.activeTab.nextElementSibling, true);
        }
      } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
        if (this.activeTab === this.tabs[0]) {
          this.activateTab(this.tabs[this.tabs.length - 1], true);
        } else {
          this.activateTab(this.activeTab.previousElementSibling, true);
        }
      }
    }

    /**
     * Saves scroll position and pauses sticky layout side-effects during a tab switch.
     */
    beginProductTabSwitch() {
      window.theme = window.theme || {};
      this._savedScrollX = window.scrollX;
      this._savedScrollY = window.scrollY;
      window.theme.tabLayoutLock = true;
      document.documentElement.classList.add('tab-switch-lock');
    }

    /**
     * Restores scroll position and updates the sticky column height for the new tab.
     */
    endProductTabSwitch() {
      window.theme.tabLayoutLock = false;
      document.documentElement.classList.remove('tab-switch-lock');
      this.syncDesktopStickyHeight();
      requestAnimationFrame(() => {
        window.scrollTo(this._savedScrollX, this._savedScrollY);
        this._savedScrollX = null;
        this._savedScrollY = null;
      });
    }

    /**
     * Updates sticky buy-box height using cached media size and new tab content height.
     */
    syncDesktopStickyHeight() {
      document.querySelectorAll('sticky-scroll-direction').forEach((sticky) => {
        if (sticky.parentElement?.dataset.stickyHeightElems) {
          sticky.syncAfterTabChange();
        }
      });
    }

    /**
     * Finishes a tab switch after layout has updated.
     */
    finishProductTabSwitch() {
      requestAnimationFrame(() => {
        window.scrollTo(this._savedScrollX, this._savedScrollY);
        requestAnimationFrame(() => {
          this.endProductTabSwitch();
        });
      });
    }

    /**
     * Activates a tab.
     * @param {Element} tab - Tab element (button).
     * @param {boolean} [manageFocus=false] - Move focus for keyboard navigation.
     */
    activateTab(tab, manageFocus = false) {
      const previousTab = this.activeTab;
      if (previousTab === tab) return;

      const lockScroll = this.isProductDetails && !manageFocus;

      if (lockScroll) {
        this.beginProductTabSwitch();
      }

      Tabs.setTabState(tab, true, this);
      tab.removeAttribute('tabindex');

      if (previousTab) {
        previousTab.setAttribute('tabindex', '-1');
        Tabs.setTabState(previousTab, false, this);
      }

      this.activeTab = tab;

      if (lockScroll) {
        this.finishProductTabSwitch();
      }

      if (manageFocus) {
        tab.focus({ preventScroll: true });
      }
    }

    /**
     * Deactivates the currently active tab.
     */
    deactivateActiveTab() {
      if (!this.activeTab) return;

      Tabs.setTabState(this.activeTab, false, this);
      this.activeTab.setAttribute('tabindex', '-1');
      this.activeTab = null;
    }

    /**
     * Sets the active state of a tab and associated content panel.
     * @param {Element} tab - Tab element (button).
     * @param {boolean} active - Set the tab as active.
     * @param {Element} root - Tabbed content root element.
     */
    static setTabState(tab, active, root = tab.closest('tabbed-content')) {
      tab.setAttribute('aria-selected', active);

      const panelId = tab.getAttribute('aria-controls');
      const panel = root
        ? root.querySelector(`#${CSS.escape(panelId)}`)
        : document.getElementById(panelId);

      if (!panel) return;

      panel.hidden = !active;
      panel.toggleAttribute('aria-hidden', !active);

      if (root?.hasAttribute('data-preserve-scroll')) {
        panel.toggleAttribute('inert', !active);
      }
    }
  }

  customElements.define('tabbed-content', Tabs);
}
